/**
 *
 * @fileoverview HttpService
 *
 * @version 1.0
 *
 * @author Milan Gotera <milangotera@gmail.com>
 *
 */

import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { NavController, ToastController } from '@ionic/angular';
import { environment } from '../../../environments/environment';
import { ModalController } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  public url: string = environment.server;

  public headers: HttpHeaders = new HttpHeaders();

  private getCache: { [url: string]: { promise: Promise<any>, timestamp: number } } = {};

  public token: string = `${localStorage.getItem('token')}`;

  constructor(
    private http: HttpClient,
    private nav: NavController,
    private toastController: ToastController,
    private modalController: ModalController,
  ) {}

  setToken() {
    this.token = `${localStorage.getItem('token')}`;
  }

  setHeaders() {
    this.setToken();
    this.headers = new HttpHeaders({
      'accept': 'application/json',
      'content-type': 'application/json',
    });

    if (this.token) {
      this.headers = this.headers.set('authorization', 'Bearer ' + this.token);
    }

    return this.headers;
  }

  private async handleUnauthorizedError() {
    // Cierra todos los modales abiertos
    const topModal = await this.modalController.getTop();
    if (topModal) {
      await topModal.dismiss();
    }

    // The session is gone: this install's FCM token stops working too (the next
    // login registers a new one).
    if (Capacitor.isNativePlatform()) {
      FirebaseMessaging.deleteToken().catch(() => undefined);
    }

    localStorage.clear();
    this.nav.navigateRoot('welcome');
  }

  post(path: string, data: unknown) {
    const simbol = path.includes('?') ? '&' : '?';
    return new Promise((resolve, reject) => {
      this.http.post(`${this.url}${path}${simbol}`, data, { headers: this.setHeaders() }).subscribe(
        (res) => {
          resolve(res);
        },
        async (error) => {
          if (error.status === 401) {
            await this.handleUnauthorizedError();
          }
          reject(error);
        },
      );
    });
  }

  get(path: string) {
    const simbol = path.includes('?') ? '&' : '?';
    const fullUrl = `${this.url}${path}${simbol}`;
    const now = Date.now();
    const cacheDuration = 5000;
  
    if (this.getCache[fullUrl] && (now - this.getCache[fullUrl].timestamp < cacheDuration)) {
      return this.getCache[fullUrl].promise;
    }
  
    const requestPromise = new Promise((resolve, reject) => {
      this.http.get(fullUrl, { headers: this.setHeaders() }).subscribe(
        (data) => {
          resolve(data);
        },
        async (error) => {
          if (error.status === 401) {
            await this.handleUnauthorizedError();
          }
          delete this.getCache[fullUrl];
          reject(error);
        }
      );
    });
  
    this.getCache[fullUrl] = {
      promise: requestPromise,
      timestamp: now
    };
  
    return requestPromise;
  }

  put(path: string, data: unknown) {
    const simbol = path.includes('?') ? '&' : '?';
    return new Promise((resolve, reject) => {
      this.http.put(`${this.url}${path}${simbol}`, data, { headers: this.setHeaders() }).subscribe(
        (res) => {
          resolve(res);
        },
        async (error) => {
          if (error.status === 401) {
            await this.handleUnauthorizedError();
          }
          reject(error);
        },
      );
    });
  }

  delete(path: string, body?: unknown) {
    const simbol = path.includes('?') ? '&' : '?';
    return new Promise((resolve, reject) => {
      this.http.delete(`${this.url}${path}${simbol}`, { headers: this.setHeaders(), body }).subscribe(
        (data) => {
          resolve(data);
        },
        async (error) => {
          if (error.status === 401) {
            await this.handleUnauthorizedError();
          }
          reject(error);
        },
      );
    });
  }
}
